import type {
  AdminCatalogBatch,
  AdminCatalogChange,
  AdminCatalogEvent,
  AdminCatalogRevertResult,
  CatalogSubject,
} from '@steado/admin-contract/catalog-history';
import { CATALOG_SUBJECT_TABLES, catalogEventKindOf } from '@steado/admin-contract/catalog-history';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { RotateCcwIcon, SearchIcon } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { TabSkeleton } from '@/components/panel/panels';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/lib/auth/auth-context';
import { useDebounced } from '@/lib/use-debounced';
import { cn } from '@/lib/utils';
import { TabError } from '@/screens/user-detail/TabError';
import {
  actorsQueryOptions,
  batchQueryOptions,
  eventByBatchQueryOptions,
  eventChangesQueryOptions,
  eventsQueryOptions,
  invalidateCatalog,
  revertBatch,
  revertChanges,
  summaryQueryOptions,
} from '@/screens/catalog-history/catalog-history-query';
import {
  SOURCE_LABELS,
  actorName,
  eventTitle,
  firstSentence,
  requestIdOf,
  withoutRequestId,
} from '@/screens/catalog-history/event-copy';
import {
  BeforeAfter,
  FilterSelect,
  Figures,
  SourceList,
  SourcesButton,
  StatusBanner,
} from '@/screens/catalog-history/history-parts';
import { inlineWhen, rowsOf, text, type HistoryRow } from '@/screens/catalog-history/history-model';
import { SourceChip } from '@/screens/catalog-history/SourceChip';
import {
  KIND_PHRASES,
  fieldValue,
  refusalText,
  unappliedLabel,
} from '@/screens/catalog-history/unapplied-copy';

import { sinceOf, type CatalogChangesSearch, type CatalogWindow } from './catalog-changes-search';

export interface CatalogChangesScreenProps {
  readonly search: CatalogChangesSearch;
  readonly onSearchChange: (next: CatalogChangesSearch) => void;
}

const ANY = '__any__';

const WINDOW_LABELS: Readonly<Record<CatalogWindow, string>> = {
  '7d': 'Last 7 days',
  today: 'Today',
  '30d': 'Last 30 days',
  all: 'All time',
};

/** How many foods or exercises the applied list shows before "Show all". */
const GROUPS_SHOWN = 4;

function time(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function dayLabel(iso: string, now: Date = new Date()): string {
  const inline = inlineWhen(iso, now);
  if (inline.startsWith('today')) return 'Today';
  if (inline.startsWith('yesterday')) return 'Yesterday';
  return inline.slice('on '.length);
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${String(n)} ${n === 1 ? one : many}`;
}

function subjectOf(table: string): CatalogSubject {
  if ((CATALOG_SUBJECT_TABLES.food as readonly string[]).includes(table)) return 'food';
  if ((CATALOG_SUBJECT_TABLES.exercise as readonly string[]).includes(table)) return 'exercise';
  return 'pattern';
}

/** The event's title and the line under it in the list. */
function listCopy(event: AdminCatalogEvent): { title: string; line: string } {
  const who = actorName(event.actor);
  const values = plural(event.valueCount, 'value');
  const subjectName = event.subjects[0]?.name ?? event.subjects[0]?.slug ?? null;
  const touched =
    event.foodCount + event.exerciseCount > 1
      ? event.exerciseCount > event.foodCount
        ? plural(event.exerciseCount, 'exercise')
        : plural(event.foodCount, 'food')
      : (subjectName ?? 'Catalog');
  switch (event.kind) {
    case 'batch': {
      if (event.source === 'curation') {
        return {
          title: 'Applied from the curation files',
          line: `${values} · ${plural(event.foodCount, 'food')}`,
        };
      }
      const batch = event.batch;
      const title = firstSentence(batch?.note ?? event.note) ?? 'Pushed batch';
      if (batch?.status === 'reverted' && batch.revertedAt !== null) {
        return { title, line: `Pushed by ${who} · ${values} · reverted at ${time(batch.revertedAt)}` };
      }
      const parts = [`Pushed by ${who}`, values];
      if ((batch?.counts.conflict ?? 0) > 0) {
        parts.push(`${String(batch?.counts.conflict ?? 0)} skipped (changed after ${who}’s copy was taken)`);
      }
      if ((batch?.counts.refused ?? 0) > 0) parts.push(`${String(batch?.counts.refused ?? 0)} refused`);
      if (event.exerciseCount > 0 && event.foodCount === 0)
        parts.push(plural(event.exerciseCount, 'exercise'));
      return { title, line: parts.join(' · ') };
    }
    case 'request':
      return {
        title: touched,
        line: [
          `Request approved by ${who}`,
          values,
          ...(event.sources.length > 0 ? [plural(event.sources.length, 'source')] : []),
        ].join(' · '),
      };
    case 'console': {
      const rows =
        event.valueCount === 1
          ? rowsOf(
              event.sample,
              subjectOf(event.sample[0]?.tableName ?? 'foods') === 'exercise' ? 'exercise' : 'food',
            )
          : [];
      const row = rows[0];
      return {
        title: touched,
        line: row === undefined ? `${values} · ${who}` : `${row.field} ${row.before} → ${row.after} · ${who}`,
      };
    }
    case 'revert':
      return {
        title: eventTitle({ kind: 'revert', source: event.source, event, values: event.valueCount }),
        line: `Reverted by ${who} · ${values} put back`,
      };
    case 'system':
      return {
        title: firstSentence(event.note) ?? 'Changed by a database update',
        line: `${touched === subjectName ? '1 food' : touched} · database update, no person`,
      };
  }
}

/**
 * Every change to the food and exercise catalog, by the action that made it:
 * console edits, approved requests, pushed batches, reverts and database
 * updates. A list by day on the left; on the right, the open event's values
 * grouped by food, what a batch skipped and what the catalog refused, and
 * Revert.
 */
export function CatalogChangesScreen({ search, onSearchChange }: CatalogChangesScreenProps) {
  const { api, can, admin } = useAuth();
  const queryClient = useQueryClient();
  const range: CatalogWindow = search.window ?? '7d';
  // Recomputed per window, not per render: a moving `since` would be a new query every time.
  const since = useMemo(() => sinceOf(range), [range]);
  const [draft, setDraft] = useState(search.q ?? '');
  const q = useDebounced(draft.trim(), 300);

  useEffect(() => {
    if ((search.q ?? '') !== q) onSearchChange({ ...search, q: q === '' ? undefined : q, event: undefined });
    // Only the settled search drives the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const filters = { kind: search.kind, actor: search.actor, since, search: search.q };
  const events = useInfiniteQuery(eventsQueryOptions(api, filters));
  const summary = useQuery(summaryQueryOptions(api, since));
  const actors = useQuery(actorsQueryOptions(api));

  const list = useMemo(() => events.data?.pages.flatMap((page) => page.items) ?? [], [events.data]);
  const selectedKey = search.event ?? list[0]?.key ?? null;
  const inList = list.find((event) => event.key === selectedKey);
  const linked = useQuery(
    eventByBatchQueryOptions(
      api,
      inList === undefined && selectedKey !== null && !selectedKey.startsWith('change:') ? selectedKey : null,
    ),
  );
  const selected = inList ?? linked.data ?? undefined;

  const [last, setLast] = useState<string | null>(null);

  const setFilter = (patch: Partial<CatalogChangesSearch>) => {
    onSearchChange({ ...search, ...patch, event: undefined });
  };

  const days: { label: string; items: AdminCatalogEvent[] }[] = [];
  for (const event of list) {
    const label = dayLabel(event.endedAt);
    const day = days[days.length - 1];
    if (day?.label === label) day.items.push(event);
    else days.push({ label, items: [event] });
  }

  // Older events load as the end of the list scrolls into view.
  const sentinel = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinel.current;
    if (node === null || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting) && events.hasNextPage && !events.isFetchingNextPage) {
        void events.fetchNextPage();
      }
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [events]);

  const s = summary.data;

  return (
    <div className="flex max-w-[1300px] flex-col gap-3.5 pb-4">
      {s === undefined ? (
        <p className="h-5" />
      ) : (
        <p className="text-[13px] text-muted-foreground">
          <span className="font-medium text-foreground">
            <Figures text={`${WINDOW_LABELS[range]}: ${plural(s.valuesChanged, 'value')} changed`} />
          </span>
          <Figures
            text={` · ${plural(s.batchesPushed, 'batch', 'batches')} pushed · ${plural(s.batchesReverted, 'batch', 'batches')} reverted · ${plural(s.valuesRefused, 'value')} refused`}
          />
        </p>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <FilterSelect
          label="Source"
          value={search.kind ?? ANY}
          options={[
            { value: ANY, label: 'All sources' },
            ...Object.entries(SOURCE_LABELS).map(([value, label]) => ({ value, label })),
          ]}
          onChange={(value) => {
            setFilter({ kind: value === ANY ? undefined : (value as CatalogChangesSearch['kind']) });
          }}
        />
        <FilterSelect
          label="Who"
          value={search.actor ?? ANY}
          options={[
            { value: ANY, label: 'Anyone' },
            ...(actors.data ?? []).map((actor) => ({
              value: actor.id,
              label: actor.name ?? actor.email ?? 'Someone',
            })),
            { value: 'system', label: 'System' },
          ]}
          onChange={(value) => {
            setFilter({ actor: value === ANY ? undefined : value });
          }}
        />
        <FilterSelect
          label="Date"
          value={range}
          options={(Object.entries(WINDOW_LABELS) as [CatalogWindow, string][]).map(([value, label]) => ({
            value,
            label,
          }))}
          onChange={(value) => {
            setFilter({ window: value === '7d' ? undefined : (value as CatalogWindow) });
          }}
        />
        <label className="relative flex max-w-[340px] min-w-[220px] flex-1 items-center">
          <span className="sr-only">Search a food or exercise</span>
          <SearchIcon
            aria-hidden="true"
            className="pointer-events-none absolute left-2.5 size-4 text-muted-foreground"
          />
          <Input
            type="search"
            value={draft}
            placeholder="Search a food or exercise"
            onChange={(changed) => {
              setDraft(changed.target.value);
            }}
            className="h-8 rounded-lg pl-8 text-[13px]"
          />
        </label>
      </div>

      {last === null ? null : (
        <StatusBanner tone="mute">
          <Figures text={last} />
        </StatusBanner>
      )}

      <div className="flex flex-wrap items-start gap-3.5">
        <section
          aria-labelledby="changes-title"
          className="min-w-0 flex-[1_1_360px] overflow-hidden rounded-xl border border-border bg-card"
        >
          <header className="flex items-center px-[18px] pt-3.5 pb-3">
            <h2 id="changes-title" className="text-[15px] font-semibold text-foreground">
              Changes
            </h2>
            <span className="ml-auto text-[12.5px] text-muted-foreground">newest first</span>
          </header>
          {events.isPending ? (
            <div className="px-[18px] pb-4">
              <TabSkeleton rows={4} />
            </div>
          ) : null}
          {events.isError ? (
            <div className="px-[18px] pb-4">
              <TabError message={events.error.message} onRetry={() => void events.refetch()} />
            </div>
          ) : null}
          {!events.isPending && list.length === 0 ? (
            <p className="border-t border-border px-[18px] py-6 text-[13px] text-muted-foreground">
              No changes in this window.
            </p>
          ) : null}
          {days.map((day) => (
            <div key={day.label}>
              <div className="border-t border-border bg-secondary px-[18px] py-2 text-[11px] font-semibold tracking-[0.055em] text-muted-foreground uppercase">
                {day.label}
              </div>
              <ul>
                {day.items.map((event) => {
                  const copy = listCopy(event);
                  const on = event.key === selected?.key;
                  return (
                    <li key={event.key} className="border-t border-border">
                      <button
                        type="button"
                        aria-pressed={on}
                        onClick={() => {
                          onSearchChange({ ...search, event: event.key });
                        }}
                        className={cn(
                          'grid w-full grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 px-[18px] py-2.5 text-left text-[13px] transition-colors',
                          on ? 'bg-secondary' : 'bg-card hover:bg-secondary/50',
                        )}
                      >
                        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                          <SourceChip kind={event.kind} />
                          <span className={cn('text-foreground', on ? 'font-semibold' : 'font-medium')}>
                            {copy.title}
                          </span>
                          {event.batch?.status === 'reverted' ? <Badge tone="mute">Reverted</Badge> : null}
                        </span>
                        <span className="font-mono text-[12px] text-muted-foreground tabular-nums">
                          {time(event.endedAt)}
                        </span>
                        <span className="col-span-full text-[12.5px] text-pretty text-muted-foreground">
                          <Figures text={copy.line} />
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
          <div
            ref={sentinel}
            className="border-t border-border bg-secondary/40 px-[18px] py-2.5 text-[12.5px] text-muted-foreground"
          >
            {events.isFetchingNextPage
              ? 'Loading older changes…'
              : events.hasNextPage
                ? 'Older changes load as you scroll.'
                : list.length === 0
                  ? ''
                  : 'That is every change in this window.'}
          </div>
        </section>

        {selected === undefined ? (
          <section className="flex min-w-0 flex-[1.5_1_540px] items-center justify-center rounded-xl border border-border bg-card px-6 py-16 text-[13px] text-muted-foreground">
            {events.isPending || linked.isFetching ? 'Loading…' : 'Pick a change to see what it did.'}
          </section>
        ) : (
          <EventDetail
            key={selected.key}
            event={selected}
            canMutate={can.mutate}
            onReverted={(message) => {
              setLast(message);
              invalidateCatalog(queryClient);
            }}
            me={admin?.displayName ?? admin?.email ?? 'You'}
          />
        )}
      </div>
    </div>
  );
}

interface EventDetailProps {
  readonly event: AdminCatalogEvent;
  readonly canMutate: boolean;
  readonly me: string;
  readonly onReverted: (message: string) => void;
}

interface Group {
  readonly slug: string;
  readonly name: string;
  readonly subject: CatalogSubject;
  readonly rows: HistoryRow[];
}

function groupsOf(changes: readonly AdminCatalogChange[], names: ReadonlyMap<string, string>): Group[] {
  const bySlug = new Map<string, AdminCatalogChange[]>();
  for (const change of changes) {
    const slug = change.slug ?? change.rowId;
    bySlug.set(slug, [...(bySlug.get(slug) ?? []), change]);
  }
  return [...bySlug.entries()].map(([slug, list]) => {
    const subject = subjectOf(list[0]?.tableName ?? 'foods');
    return {
      slug,
      subject,
      name:
        names.get(slug) ??
        text(list.find((c) => c.tableName === 'foods' || c.tableName === 'exercises')?.record?.name, slug),
      rows: rowsOf(list, subject === 'exercise' ? 'exercise' : 'food'),
    };
  });
}

function EventDetail({ event, canMutate, me, onReverted }: EventDetailProps) {
  const { api } = useAuth();
  // A batch row: a pushed batch, or an approval its writer recorded as one. Either reverts whole, server-side.
  const hasBatchRow = event.batch !== null;
  const isPushed = event.kind === 'batch' && hasBatchRow;
  const batch = useQuery(batchQueryOptions(api, hasBatchRow ? event.batchId : null));
  const others = useQuery(eventChangesQueryOptions(api, !hasBatchRow ? event.batchId : null));
  const [confirming, setConfirming] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [reverted, setReverted] = useState(false);
  const confirmId = useId();
  const sourcesId = useId();

  const changes: readonly AdminCatalogChange[] = hasBatchRow
    ? (batch.data?.changes ?? [])
    : event.batchId === null
      ? event.sample
      : (others.data ?? []);
  const isLoading = hasBatchRow ? batch.isPending : event.batchId !== null && others.isPending;
  const names = new Map(
    event.subjects.flatMap((subject) =>
      subject.name === null ? [] : [[subject.slug, subject.name] as const],
    ),
  );
  const groups = groupsOf(changes, names);
  const shown = showAll ? groups : groups.slice(0, GROUPS_SHOWN);
  const hiddenValues = groups.slice(GROUPS_SHOWN).reduce((sum, group) => sum + group.rows.length, 0);
  const who = actorName(event.actor);
  const title = listCopy(event).title;
  const requestId = requestIdOf(event, event.source);
  const detail: AdminCatalogBatch | undefined = batch.data ?? event.batch ?? undefined;
  const skipped = (detail?.unapplied ?? []).filter((entry) => entry.status === 'conflict');
  const refused = (detail?.unapplied ?? []).filter((entry) => entry.status !== 'conflict');
  const isReverted = reverted || event.batch?.status === 'reverted';
  const everyId = changes.map((change) => change.id);
  const complete = everyId.length >= event.changeCount;

  const revert = useMutation({
    mutationFn: (): Promise<AdminCatalogRevertResult> =>
      hasBatchRow && event.batchId !== null ? revertBatch(api, event.batchId) : revertChanges(api, everyId),
    onSuccess: (result) => {
      setConfirming(false);
      setReverted(true);
      const left = [
        ...(result.skipped.length > 0
          ? [`${plural(result.skipped.length, 'value')} changed since stayed`]
          : []),
        ...(result.refused.length > 0
          ? [`${plural(result.refused.length, 'food')} would have failed the checks and stayed`]
          : []),
      ];
      onReverted(
        `Reverted by ${me} at ${time(new Date().toISOString())}: ${title}. ${left.length > 0 ? `${left.join('; ')}. ` : ''}Saved as the newest change in History.`,
      );
    },
    retry: false,
  });

  const single = event.kind === 'console' && event.valueCount === 1 ? groups[0]?.rows[0] : undefined;
  const revertLabel = isPushed
    ? 'Revert batch'
    : event.kind === 'revert'
      ? 'Undo this revert'
      : event.valueCount === 1
        ? 'Revert'
        : `Revert all ${String(event.valueCount)}`;
  const revertNote = isPushed
    ? `Puts back the ${String(event.valueCount)} values this batch changed, as one new change in History. Values changed again since, and reverts that would fail the catalog’s checks, are skipped and listed.`
    : event.kind === 'revert'
      ? 'Puts back what this revert took off, as a new change in History.'
      : single !== undefined
        ? `${single.field} goes back to ${single.before}, as a new change in History.`
        : `${event.foodCount + event.exerciseCount === 1 ? (groups[0]?.name ?? 'It') : 'These'} ${event.foodCount + event.exerciseCount === 1 ? 'goes' : 'go'} back to ${event.foodCount + event.exerciseCount === 1 ? 'its' : 'their'} values from before ${time(event.startedAt)}, as one new change in History.`;
  const canRevert = !isReverted && (hasBatchRow || complete) && event.changeCount > 0;

  const footer = isReverted
    ? isPushed
      ? 'This batch was reverted. Its values are no longer in the catalog.'
      : 'Reverted. The revert is the newest change in History.'
    : {
        batch:
          'Revert batch puts back every value this batch changed. Values changed again since then stay, because the newer change wins. Reverts that would fail the catalog’s checks, such as energy without its macros, are skipped and listed.',
        request: `Each of these can also be reverted one at a time from ${groups[0]?.name ?? 'the food'}’s History.`,
        console: 'A later change to this field would win over a revert of this one.',
        revert: 'A revert is a change like any other: it is in History and can itself be reverted.',
        system: 'System changes are writes no person made, such as a database update.',
      }[event.kind];

  const meta = isPushed
    ? `Pushed by ${who} ${inlineWhen(event.endedAt).replace(/^on /, 'on ')} · from ${who}’s copy of the catalog, taken ${inlineWhen(event.batch?.base ?? event.startedAt)}`
    : event.kind === 'request'
      ? `Approved by ${who} ${inlineWhen(event.endedAt)}`
      : event.kind === 'console'
        ? `Edited by ${who} in the console ${inlineWhen(event.endedAt)}`
        : event.kind === 'revert'
          ? `Reverted by ${who} ${inlineWhen(event.endedAt)}`
          : `Written by a database update ${inlineWhen(event.endedAt)}`;

  const stats: { n: number; label: string; tone?: 'warn' | 'err' }[] = isPushed
    ? [
        { n: event.valueCount, label: 'values applied' },
        {
          n: event.batch?.counts.conflict ?? 0,
          label: 'skipped',
          ...((event.batch?.counts.conflict ?? 0) > 0 ? { tone: 'warn' as const } : {}),
        },
        {
          n: event.batch?.counts.refused ?? 0,
          label: 'refused',
          ...((event.batch?.counts.refused ?? 0) > 0 ? { tone: 'err' as const } : {}),
        },
        event.exerciseCount > event.foodCount
          ? { n: event.exerciseCount, label: 'exercises' }
          : { n: event.foodCount, label: 'foods' },
      ]
    : [
        {
          n: event.valueCount,
          label:
            event.kind === 'revert'
              ? event.valueCount === 1
                ? 'value put back'
                : 'values put back'
              : event.valueCount === 1
                ? 'value'
                : 'values',
        },
        ...(event.foodCount > 1 ? [{ n: event.foodCount, label: 'foods' }] : []),
        ...(event.exerciseCount > 1 ? [{ n: event.exerciseCount, label: 'exercises' }] : []),
        ...(event.sources.length > 0
          ? [{ n: event.sources.length, label: event.sources.length === 1 ? 'source' : 'sources' }]
          : []),
      ];

  return (
    <section
      aria-labelledby="detail-title"
      className="min-w-0 flex-[1.5_1_540px] overflow-hidden rounded-xl border border-border bg-card"
    >
      <div className="flex flex-wrap items-start gap-3 px-[18px] pt-4 pb-3.5">
        <div className="flex min-w-0 flex-[1_1_320px] flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <SourceChip kind={event.kind} />
            {isReverted ? <Badge tone="mute">Reverted</Badge> : null}
          </div>
          <h2 id="detail-title" className="text-[17px] font-semibold tracking-[-0.01em] text-foreground">
            {title}
          </h2>
          <p className="text-[12.5px] text-muted-foreground">
            <Figures text={meta} />
          </p>
          {requestId === null ? null : (
            <Link
              to="/food-requests/$requestId"
              params={{ requestId }}
              className="inline-flex min-h-6 items-center self-start text-[13px] font-medium text-info-fg underline-offset-2 hover:underline"
            >
              Open the food request
            </Link>
          )}
        </div>
        {canRevert ? (
          <Button
            variant="outline"
            className="border-err-fg/30 text-err-fg hover:bg-err-bg hover:text-err-fg"
            disabled={!canMutate || revert.isPending}
            title={canMutate ? undefined : 'Reverting requires the admin role.'}
            aria-expanded={confirming}
            onClick={() => {
              setConfirming(true);
            }}
          >
            <RotateCcwIcon aria-hidden="true" />
            {revertLabel}
          </Button>
        ) : null}
      </div>

      {confirming ? (
        <div
          role="alertdialog"
          aria-labelledby={confirmId}
          className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border bg-err-bg/40 px-[18px] py-2.5"
        >
          <span className="flex min-w-[18rem] flex-1 flex-col gap-0.5">
            <span id={confirmId} className="text-[13px] font-medium text-foreground">
              {revertLabel}?
            </span>
            <span className="text-[12.5px] text-pretty text-muted-foreground">
              <Figures text={revertNote} />
            </span>
            {revert.isError ? (
              <span role="alert" className="text-[12.5px] font-medium text-err-fg">
                {revert.error.message}
              </span>
            ) : null}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={revert.isPending}
            onClick={() => {
              setConfirming(false);
              revert.reset();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-err-fg/30 text-err-fg hover:bg-err-bg hover:text-err-fg"
            disabled={revert.isPending}
            autoFocus
            onClick={() => {
              revert.mutate();
            }}
          >
            {revert.isPending ? 'Reverting…' : revertLabel}
          </Button>
        </div>
      ) : null}

      <div className="grid grid-cols-[repeat(auto-fit,minmax(110px,1fr))] border-t border-border [&>*:last-child]:border-r-0">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="flex flex-col gap-0.5 border-r border-border px-[18px] pt-2.5 pb-3"
          >
            <span
              className={cn(
                'font-mono text-[13px] font-medium tabular-nums',
                stat.tone === 'warn'
                  ? 'text-warn-fg'
                  : stat.tone === 'err'
                    ? 'text-err-fg'
                    : 'text-foreground',
              )}
            >
              {stat.n}
            </span>
            <span className="text-[12.5px] text-muted-foreground">{stat.label}</span>
          </div>
        ))}
      </div>

      {event.note === null ? null : (
        <div className="flex flex-col gap-0.5 border-t border-border px-[18px] pt-2.5 pb-3">
          <span className="text-[12.5px] font-medium text-muted-foreground">Note</span>
          <p className="max-w-[75ch] text-[13px] text-pretty text-foreground">
            {withoutRequestId(event.note)}
          </p>
          {event.sources.length === 0 ? null : (
            <>
              <span className="-ml-1.5">
                <SourcesButton
                  count={event.sources.length}
                  open={sourcesOpen}
                  controls={sourcesId}
                  label={`${sourcesOpen ? 'Hide' : 'Show'} ${plural(event.sources.length, 'source')}`}
                  onToggle={() => {
                    setSourcesOpen((open) => !open);
                  }}
                />
              </span>
              {sourcesOpen ? <SourceList id={sourcesId} sources={event.sources} /> : null}
            </>
          )}
        </div>
      )}

      <div className="grid grid-cols-[minmax(150px,0.9fr)_minmax(0,1.1fr)] gap-3 border-t border-border bg-secondary px-[18px] py-2 text-[11px] font-semibold tracking-[0.055em] text-muted-foreground uppercase">
        <span>{event.kind === 'revert' ? 'Put back, by food' : 'Applied, by food'}</span>
        <span>Before → after</span>
      </div>
      {isLoading ? (
        <div className="px-[18px] py-3">
          <TabSkeleton rows={2} />
        </div>
      ) : null}
      {shown.map((group) => (
        <div key={group.slug} className="border-t border-border pb-2">
          <div className="flex flex-wrap items-baseline gap-x-2 px-[18px] pt-2.5 pb-0.5">
            <h3 className="text-[13px] font-semibold text-foreground">
              {group.subject === 'food' ? (
                <Link
                  to="/foods/$foodId"
                  params={{ foodId: foodIdOf(group.rows[0]?.changes[0]) }}
                  search={{ tab: 'history' }}
                  className="underline-offset-2 hover:underline"
                >
                  {group.name}
                </Link>
              ) : (
                group.name
              )}
            </h3>
            <span className="text-[12.5px] text-muted-foreground">{plural(group.rows.length, 'value')}</span>
          </div>
          {group.rows.map((row) => (
            <div
              key={row.key}
              className="grid grid-cols-[minmax(150px,0.9fr)_minmax(0,1.1fr)] items-baseline gap-3 px-[18px] py-0.5 text-[13px]"
            >
              <span className="text-muted-foreground">{row.field}</span>
              <BeforeAfter before={row.before} after={row.after} />
            </div>
          ))}
        </div>
      ))}
      {!isLoading && groups.length > GROUPS_SHOWN && !showAll ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-border bg-secondary/40 px-[18px] py-1.5">
          <span className="min-w-[15rem] flex-1 text-[12.5px] text-muted-foreground">
            <Figures
              text={`${plural(hiddenValues, 'more value', 'more values')} in ${plural(groups.length - GROUPS_SHOWN, 'food')}`}
            />
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setShowAll(true);
            }}
          >
            Show all {event.valueCount}
          </Button>
        </div>
      ) : null}

      {skipped.length === 0 ? null : (
        <>
          <div className="flex flex-col gap-0.5 border-t border-input/60 bg-warn-bg/60 px-[18px] pt-3 pb-2">
            <h3 className="text-[13px] font-semibold text-foreground">
              Skipped: changed after {who}’s copy was taken{' '}
              <span className="font-mono font-medium text-warn-fg">{skipped.length}</span>
            </h3>
            <p className="max-w-[75ch] text-[12.5px] text-muted-foreground">
              The newer change stays; the batch&apos;s value was not applied.
            </p>
          </div>
          {skipped.map((entry) =>
            (entry.fields ?? [{ field: entry.domain, before: null, after: null, conflict: null }]).map(
              (field) => (
                <div
                  key={`${entry.domain}:${entry.key}:${field.field}`}
                  className="grid grid-cols-[minmax(150px,0.9fr)_minmax(0,1.1fr)] items-baseline gap-3 border-t border-border px-[18px] py-2 text-[13px]"
                >
                  <span className="flex min-w-0 flex-col gap-px">
                    <span className="font-medium text-foreground">{entry.name ?? entry.key}</span>
                    <span className="text-[12.5px] text-muted-foreground">
                      {unappliedLabel({ ...entry, fields: [field] })}
                    </span>
                  </span>
                  <span className="flex min-w-0 flex-col gap-px">
                    <span>
                      Kept <Figures className="font-medium" text={fieldValue(field.field, field.before)} />{' '}
                      {field.conflict === null ? null : (
                        <span className="text-[12.5px] text-muted-foreground">
                          <Figures
                            text={`from ${KIND_PHRASES[catalogEventKindOf(field.conflict.source)]}, ${actorName(field.conflictActor ?? null)}, ${inlineWhen(field.conflict.changedAt).replace(/^on /, '').replace(' at ', ' ')}`}
                          />
                        </span>
                      )}
                    </span>
                    <span className="text-[12.5px] text-muted-foreground">
                      Batch wanted <Figures text={fieldValue(field.field, field.after)} />
                    </span>
                  </span>
                </div>
              ),
            ),
          )}
        </>
      )}

      {refused.length === 0 ? null : (
        <>
          <div className="flex flex-col gap-0.5 border-t border-input/60 bg-err-bg/60 px-[18px] pt-3 pb-2">
            <h3 className="text-[13px] font-semibold text-foreground">
              Refused: failed the catalog&apos;s checks{' '}
              <span className="font-mono font-medium text-err-fg">{refused.length}</span>
            </h3>
            <p className="max-w-[75ch] text-[12.5px] text-muted-foreground">
              The same checks as the console. Nothing was written for these; the rest of the batch went in.
            </p>
          </div>
          {refused.map((entry) => (
            <div
              key={`${entry.domain}:${entry.key}`}
              className="grid grid-cols-[minmax(150px,0.9fr)_minmax(0,1.1fr)] items-baseline gap-3 border-t border-border px-[18px] py-2 text-[13px]"
            >
              <span className="flex min-w-0 flex-col gap-px">
                <span className="font-medium break-words text-foreground">{entry.name ?? entry.key}</span>
                <span className="text-[12.5px] text-muted-foreground">{unappliedLabel(entry)}</span>
              </span>
              <span className="flex min-w-0 flex-col gap-px">
                {(entry.fields ?? []).length > 0 ? (
                  <span>
                    Batch had{' '}
                    <Figures
                      text={(entry.fields ?? [])
                        .map((field) => fieldValue(field.field, field.after))
                        .join(' + ')}
                    />
                  </span>
                ) : null}
                <span className="text-[12.5px] text-pretty text-err-fg">
                  {entry.status === 'unknown'
                    ? `The catalog has nothing called ${entry.key}.`
                    : entry.status === 'skipped_flagged'
                      ? 'Flagged for a second look, so it was not applied.'
                      : entry.status === 'skipped_unreviewed'
                        ? 'Nobody reviewed it, so it was not applied.'
                        : entry.reasons.map((reason) => refusalText(reason, entry.key)).join(' ')}
                </span>
              </span>
            </div>
          ))}
        </>
      )}

      <p className="border-t border-border bg-secondary/40 px-[18px] py-3 text-[12.5px] text-pretty text-muted-foreground">
        {footer}
      </p>
    </section>
  );
}

/** The food a change belongs to: the row itself, or the parent of a portion, name or recipe. */
function foodIdOf(change: AdminCatalogChange | undefined): string {
  if (change === undefined) return '';
  if (change.tableName === 'foods') return change.rowId;
  const record = change.record ?? change.oldRecord ?? {};
  return text(record.food_id ?? record.dish_id);
}
